/*
 * Copyright © 2017-2020 Skale Education Inc. - All Rights Reserved
 */
import { RecursivePartial, SnakeToCamelCase } from "config/UtilityTypes";
import {
  GetSessionSecretQuery,
  LeaderSummaryType,
} from "graphql/public/generated_public";
import {
  SummitType,
  GetMemberUserListQuery,
  GetUserproxiesListQuery,
  GetMeQuery,
  GetStaffSessionSecretQuery,
  GetSkaleSessionQuery,
} from "graphql/internal/generated";

export interface SelfObjectWithoutOrg {
  firstName: string;
  lastName: string;
  userId: string;
  channelName: any;
  storeName: string;
  profileImgLgUrl: string;
  profileImgSmUrl: string;
  districtId: string;
  score: number;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  isTrainer: boolean;
  isCoach: boolean;
  title?: string;
  leaderSummary: RecursivePartial<LeaderSummaryType>;
  primaryEmail: string;
}

export type SelectedSkillObjectType = NonNullable<
  GetSkaleSessionQuery["skalesession"]
>["skill"] & {
  sessionSteps: any[];
};

type Organization = NonNullable<GetMeQuery["user"]["organization"]>;

export type SelfObject = SelfObjectWithoutOrg & {
  [key in keyof Omit<
    Organization,
    "__typename" | "orgCode"
  > as `organization${Capitalize<key>}`]: Organization[key];
} & {
  organizationAllowQueueReentry: Organization["isOpen"];
  organizationCode: Organization["orgCode"];
  organizationType: Organization["orgType"];
  organizationIsPerformanceEnabled: undefined;
};

export type VideoInfoType =
  | (NonNullable<
      | GetStaffSessionSecretQuery["staffSession"]
      | GetSessionSecretQuery["session"]
    > & { transcriptJson?: any } & { organization?: Organization })
  | null
  | undefined;

export type DisplayedSummitView =
  | "Summit"
  | "Match"
  | "Chat"
  | "SessionFinish"
  | "DeviceSwitch";

export type FullDataQueryType = "session_count" | "duration";

function field<T>(t: T) {
  return t;
}

// prettier-ignore
export const DEFAULT_STORE = {
  // SESSIONS SECTION
  CHAT_FAILED:                        field(false),
  CHAT_CONNECTED:                     field(false),
  CHAT_PARTNER_HAS_CANCELLED:         field(true),
  MATCH_OBJECT:                       field<any>({}),
  SKILL_OBJECT:                       field<SelectedSkillObjectType | {}>({}),
  SKALE_SESSION_OBJECT:               field<any>({}),
  CHAT_AGENDA_MODAL_VISIBLE:          field(true),
  DISPLAYED_SUMMIT_VIEW:              field<DisplayedSummitView>("Summit"),
  AUTO_JOIN_SUMMIT:                   field(false),
  PROFILE_PIC_SET:                    field(false),
  PARTNER_OBJECT:                     field<any>({}),
  IS_POST_EVENT:                      field(false),
  MATCH_ACK:                          field(false),
  CHAT_BEGIN_BUTTON_PRESSED:          field(false),
  IS_SELF_CHAT_READY:                 field(false),
  IS_PARTNER_CHAT_READY:              field(false),
  IS_QUEUE_BEACON_ACTIVATED:          field(false),
  SUMMIT_SKILL_LIST:                  field<any[]>([]),
  PAST_SUMMIT_LIST:                   field<any[]>([]),
  ACTIVE_SUMMIT_LIST:                 field<SummitType[]>([]),
  // TODO: Type this entry's empty case properly and responsibly (RecursivePartial, etc.)
  SELECTED_SUMMIT:                    field<SummitType>({} as SummitType),
  // END SESSIONS SECTION
  ACTIVITY_OBJECT:                    field<any>({}),
  SKILL_PROGRESS_USER_LIST:           field<any[]>([]),
  SKILL_PROGRESS_STORE_LIST:          field<any[]>([]),
  CURRICULUM_STAT_SKILL_LIST:         field<any[]>([]),
  CURRICULUM_STAT_SKILL_MODULE_LIST:  field<any[]>([]),
  ONBOARDING_STORE_LIST:              field<any[]>([]),
  ONBOARDING_USER_LIST:               field<any[]>([]),
  MEMBER_USER_LIST:                   field<GetMemberUserListQuery["staffUsers"]>({edges: [], totalCount: 0, pageInfo: {hasNextPage: false}}),
  UNREGISTERED_USERPROXIES_LIST:      field<GetUserproxiesListQuery["staffUserproxies"]>({edges: [], totalCount: 0, pageInfo: {hasNextPage: false}}),
  SESSION_PROGRESS_STORE_LIST:        field<any[]>([]),
  SESSION_PROGRESS_USER_LIST:         field<any[]>([]),
  START_OBJECT:                       field<any>({}),
  PRIMARY_AUTH_TOKEN:                 field(""),
  PRIMARY_DETAILS:                    field<any>({}),
  PRIMARY_MEMBER_LIST:                field<any[]>([]),
  AUTH_TOKEN:                         field(""),
  DAY_RANGE_START:                    field<Date | null>(null),
  DAY_RANGE_END:                      field<Date | null>(null),
  FILTER_DISTRICT_ID:                 field<any>(null),
  FILTER_REGION_ID:                   field<any>(null),
  FILTER_SKILL_ID:                    field<any>(null),
  FILTER_STORE_ID:                    field<any>(null),
  FILTER_USER_ID:                     field<any>(null),
  ANNOUNCEMENT_OBJECT:                field<any>({}),
  REGION_LIST:                        field<any[]>([]),
  DISTRICT_LIST:                      field<any[]>([]),
  STORE_LIST:                         field<any[]>([]),
  SELF_OBJECT:                        field<SelfObject>({} as any), // TODO fix refactor this type
  SKILL_SESSION_STEPS:                field<any[]>([]),
  SKILL_DESCRIPTION:                  field(""),
  SKILL_MODULE_DESCRIPTION:           field(""),
  SKILL_LIST:                         field<any[]>([]),
  PROPRIETARY_POPUP_MODAL_IS_VISIBLE: field(false),
  CURRICULUM:                         field<any[]>([]),
  SKILL_NAME:                         field(""),
  SKILL_MODULE_NAME:                  field(""),
  SUMMIT_NAME:                        field(""),
  SUMMIT_DESCRIPTION:                 field(""),
  HAS_EXITED_SUMMIT:                  field<any>({}),
  LAST_COMPLETED_SUMMIT:              field(""),
  VIDEO_INFO:                         field<VideoInfoType>(null),
  TIME_SERIES_DATA:                   field<any>(null),
  FULL_DATA_QUERY_TYPE:               field<FullDataQueryType>("session_count"),
  MOST_RECENT_MATCH_ALGORITHM:        field<any>(null),
  // Performance
  METRIC_METADATA_LIST:               field<any[]>([]),
  METRIC_LIST:                        field<any[]>([]),
  UPLOADED_CSV_DATA_LIST:             field<any>(null),
  FILE_INFO_OBJECT:                   field<any>({}),
  SUMMIT_SESSION_LIST:                field<any>({}),
  SUMMIT_FEEDBACK_LIST:               field<any>({}),
  SUMMIT_ATTENDANCE_LIST:             field<any>({}),
  // RSVP
  SHOW_RSVP_MODAL:                    field(false),
  // Activity Table
  SESSION_INFO_OBJECT:                field<any>({}),
  TRANSCRIPTION_OBJECT:               field<any>({}),
  TRANSCRIPTION_KEYWORD:              field<string | null>(""),
  // Manager Dashboard
  CURRENT_LOCATION:                   field<any>({})
};

export type RootState = {
  [Key in keyof typeof DEFAULT_STORE as SnakeToCamelCase<Key>]: typeof DEFAULT_STORE[Key];
};
